/**
 * Leave Decision Classifier — pure functions, no React.
 *
 * Maps the SIX possible backend values of `hr_action` and the FOUR possible
 * values of `manager_action` onto the correct display state for the Leave
 * Admin table.
 *
 * The bug this replaces: the previous badge code hard-checked
 * `hr_action === 'approve'`, which mislabelled every HR override/bypass
 * approval as "Rejected".
 *
 * Backend truth (see /app/backend/services/leave_service.py):
 *   hr_action ∈ {
 *     "approve",         // normal HR approval on pending_hr
 *     "reject",          // normal HR rejection on pending_hr
 *     "bypass_approve",  // RACI-bypass approval
 *     "bypass_reject",   // RACI-bypass rejection
 *     "override_approve",// HR override on a stuck pending_manager request
 *     "override_reject", // HR override rejection on pending_manager
 *     null               // HR has not acted yet (or state pre-HR)
 *   }
 *   manager_action ∈ { "approve", "reject", null }
 *
 * Return shape from both classifiers:
 *   {
 *     kind:     'approved' | 'rejected' | 'pending' | 'skipped' | 'none',
 *     tone:     'green' | 'red' | 'amber' | 'slate',
 *     label:    <string, English default>,
 *     label_ar: <string, Arabic>,
 *     sub:      <string | null> — small badge suffix like 'Override' / 'Bypass'
 *     sub_ar:   <string | null>,
 *     tooltip:  {
 *       title:        <string>,
 *       title_ar:     <string>,
 *       actor_name:   <string | null>,
 *       actor_email:  <string | null>,
 *       timestamp:    <ISO string | null>,
 *       reason:       <string | null>,
 *     } | null,
 *   }
 */

const _isNonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const _fmtActor = (req, kind /* 'manager' | 'hr' */) => {
  if (kind === 'manager') {
    const name = _isNonEmpty(req.manager_action_by_name) ? String(req.manager_action_by_name).trim() : null;
    const email = _isNonEmpty(req.manager_action_by_email) ? req.manager_action_by_email : null;
    return { actor_name: name, actor_email: email };
  }
  const name = _isNonEmpty(req.hr_action_by_name) ? String(req.hr_action_by_name).trim() : null;
  const email = _isNonEmpty(req.hr_action_by_email) ? req.hr_action_by_email : null;
  return { actor_name: name, actor_email: email };
};

/**
 * Classify the MANAGER column display for one leave request row.
 * Truthful for the six possible upstream shapes.
 */
export function classifyManagerAction(request) {
  const req = request || {};
  const managerAction = req.manager_action;
  const status = req.status;
  const hrAction = req.hr_action;

  // 1. Manager explicitly acted.
  if (managerAction === 'approve') {
    const actor = _fmtActor(req, 'manager');
    return {
      kind: 'approved',
      tone: 'green',
      label: 'Approved',
      label_ar: 'موافق',
      sub: null,
      sub_ar: null,
      tooltip: {
        title: 'Manager approved',
        title_ar: 'وافق المدير',
        actor_name: actor.actor_name,
        actor_email: actor.actor_email,
        timestamp: req.manager_action_at || null,
        reason: _isNonEmpty(req.manager_reason) ? req.manager_reason : null,
      },
    };
  }

  if (managerAction === 'reject') {
    const actor = _fmtActor(req, 'manager');
    return {
      kind: 'rejected',
      tone: 'red',
      label: 'Rejected',
      label_ar: 'مرفوض',
      sub: null,
      sub_ar: null,
      tooltip: {
        title: 'Manager rejected',
        title_ar: 'رفض المدير',
        actor_name: actor.actor_name,
        actor_email: actor.actor_email,
        timestamp: req.manager_action_at || null,
        reason:
          (_isNonEmpty(req.manager_reason) ? req.manager_reason : null) ||
          (_isNonEmpty(req.rejection_reason) ? req.rejection_reason : null),
      },
    };
  }

  // 2. Manager did not act but HR bypassed / overrode the manager step.
  //    → show a clear "Skipped" chip so it's obvious the manager was
  //    intentionally bypassed, not that the record is broken.
  if (!managerAction && typeof hrAction === 'string') {
    if (hrAction.startsWith('override_')) {
      return {
        kind: 'skipped',
        tone: 'slate',
        label: 'Skipped',
        label_ar: 'تم تخطيه',
        sub: 'HR Override',
        sub_ar: 'تجاوز HR',
        tooltip: {
          title: 'Manager step skipped — HR used Override',
          title_ar: 'تم تخطي خطوة المدير — HR استخدم التجاوز',
          actor_name: null,
          actor_email: null,
          timestamp: null,
          reason: _isNonEmpty(req.hr_override_reason) ? req.hr_override_reason : null,
        },
      };
    }
    if (hrAction.startsWith('bypass_')) {
      return {
        kind: 'skipped',
        tone: 'slate',
        label: 'Skipped',
        label_ar: 'تم تخطيه',
        sub: 'RACI Bypass',
        sub_ar: 'تجاوز RACI',
        tooltip: {
          title: 'Manager step skipped — RACI bypass',
          title_ar: 'تم تخطي خطوة المدير — تجاوز RACI',
          actor_name: null,
          actor_email: null,
          timestamp: null,
          reason: _isNonEmpty(req.raci_bypass_reason) ? req.raci_bypass_reason : null,
        },
      };
    }
  }

  // 3. Manager not acted, waiting.
  if (status === 'pending_manager') {
    return {
      kind: 'pending',
      tone: 'amber',
      label: 'Pending',
      label_ar: 'قيد الانتظار',
      sub: null,
      sub_ar: null,
      tooltip: {
        title: 'Awaiting manager decision',
        title_ar: 'بانتظار قرار المدير',
        actor_name: null,
        actor_email: null,
        timestamp: null,
        reason: null,
      },
    };
  }

  // 4. Nothing to say (draft, cancelled, or unknown shape).
  return {
    kind: 'none',
    tone: 'slate',
    label: '–',
    label_ar: '–',
    sub: null,
    sub_ar: null,
    tooltip: null,
  };
}

/**
 * Classify the HR ACTION column display for one leave request row.
 * Handles every real value the backend can write to hr_action.
 */
export function classifyHrAction(request) {
  const req = request || {};
  const hrAction = req.hr_action;
  const status = req.status;

  // Only enter this classifier when the row isn't currently actionable
  // (LeaveAdminPage handles the "canAct" / "canOverride" button branches
  // higher up in the render tree). This function is called for rows in a
  // terminal or waiting state.

  if (typeof hrAction === 'string' && hrAction) {
    const isApprove = hrAction.endsWith('approve');
    const isReject = hrAction.endsWith('reject');
    let sub = null;
    let sub_ar = null;
    if (hrAction.startsWith('override_')) {
      sub = 'Override';
      sub_ar = 'تجاوز';
    } else if (hrAction.startsWith('bypass_')) {
      sub = 'Bypass';
      sub_ar = 'تجاوز RACI';
    }
    const actor = _fmtActor(req, 'hr');

    if (isApprove) {
      return {
        kind: 'approved',
        tone: 'green',
        label: 'Approved',
        label_ar: 'موافق',
        sub,
        sub_ar,
        tooltip: {
          title: sub ? `HR approved (${sub})` : 'HR approved',
          title_ar: sub_ar ? `وافق HR (${sub_ar})` : 'وافق HR',
          actor_name: actor.actor_name,
          actor_email: actor.actor_email,
          timestamp: req.hr_action_at || null,
          reason:
            (_isNonEmpty(req.hr_override_reason) ? req.hr_override_reason : null) ||
            (_isNonEmpty(req.raci_bypass_reason) ? req.raci_bypass_reason : null) ||
            (_isNonEmpty(req.hr_reason) ? req.hr_reason : null),
        },
      };
    }
    if (isReject) {
      return {
        kind: 'rejected',
        tone: 'red',
        label: 'Rejected',
        label_ar: 'مرفوض',
        sub,
        sub_ar,
        tooltip: {
          title: sub ? `HR rejected (${sub})` : 'HR rejected',
          title_ar: sub_ar ? `رفض HR (${sub_ar})` : 'رفض HR',
          actor_name: actor.actor_name,
          actor_email: actor.actor_email,
          timestamp: req.hr_action_at || null,
          reason:
            (_isNonEmpty(req.hr_override_reason) ? req.hr_override_reason : null) ||
            (_isNonEmpty(req.raci_bypass_reason) ? req.raci_bypass_reason : null) ||
            (_isNonEmpty(req.hr_reason) ? req.hr_reason : null) ||
            (_isNonEmpty(req.rejection_reason) ? req.rejection_reason : null),
        },
      };
    }
    // Unknown hr_action string — treat as pending, don't silently mislabel.
    return {
      kind: 'pending',
      tone: 'amber',
      label: 'Pending',
      label_ar: 'قيد الانتظار',
      sub: null,
      sub_ar: null,
      tooltip: {
        title: `Unknown hr_action: ${hrAction}`,
        title_ar: `قيمة hr_action غير معروفة: ${hrAction}`,
        actor_name: null,
        actor_email: null,
        timestamp: null,
        reason: null,
      },
    };
  }

  // hr_action is null / absent.
  if (status === 'pending_hr') {
    return {
      kind: 'pending',
      tone: 'amber',
      label: 'Pending',
      label_ar: 'قيد الانتظار',
      sub: null,
      sub_ar: null,
      tooltip: {
        title: 'Awaiting HR decision',
        title_ar: 'بانتظار قرار HR',
        actor_name: null,
        actor_email: null,
        timestamp: null,
        reason: null,
      },
    };
  }

  if (status === 'pending_manager') {
    return {
      kind: 'pending',
      tone: 'amber',
      label: 'Pending',
      label_ar: 'قيد الانتظار',
      sub: 'Manager first',
      sub_ar: 'المدير أولاً',
      tooltip: {
        title: 'HR review starts after manager approval',
        title_ar: 'تبدأ مراجعة HR بعد موافقة المدير',
        actor_name: null,
        actor_email: null,
        timestamp: null,
        reason: null,
      },
    };
  }

  // Rejected by manager (never reached HR) or other terminal states.
  return {
    kind: 'none',
    tone: 'slate',
    label: '–',
    label_ar: '–',
    sub: null,
    sub_ar: null,
    tooltip: null,
  };
}

/** Tone → Tailwind class mapping. Kept here so the classifier is the
 * single source of truth and the JSX stays declarative. */
export const TONE_TO_BADGE_CLASS = {
  green: 'bg-green-100 text-green-800 border-green-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
};
